// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { isMobileScreen } from "@eisinc/XDL/Utilities";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContext from "@insite/mobius/GridContainer/GridContext";
import { GridItemStyle } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import styled, { css, ThemeContext } from "styled-components";

export type GridContainerProps = MobiusStyledComponentProps<
    "div",
    {
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<GridContainerProps>;
        /** The amount of space between grid items, in pixels. */
        gap?: number;
        /** Props to be passed to grid offset. */
        offsetProps?: MobiusStyledComponentProps<"div">;
        /** CSS string or styled-components function to be injected into this component. */
        offsetCss?: StyledProp<GridContainerProps>;
        /** If it is full width then do not apply max-width to the grid container. */
        isFullWidth?: boolean;
        /** XNG-Changes: The amount of space between grid items on mobile only, in pixels. */
        gapMobile?: number;
    }
>;

const GridOffset = styled.div<{ gap: number }>`
    margin: ${({ gap }) => -gap / 2}px;
    ${injectCss}
`;

const GridWrapper = styled.div<GridContainerProps>`
    flex-grow: 1;
    width: 100%;

    // margin: 0 auto;

    ${({ theme, isFullWidth }: GridContainerProps & { theme: BaseTheme }) => {
        if (isFullWidth) {
            return "max-width: 100%;";
        }

        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    @media print {
        max-width: 100%;
    }

    // XNG-Changes
    & > ${GridOffset} {
        display: flex;
        flex-wrap: wrap;
        width: ${({ gap }) => ((gap as number) > 0 ? `calc(100% + ${gap}px)` : "100%")};

        @media print {
            display: block;
        }
    }
    ${GridItemStyle} {
        box-sizing: border-box;
        margin: 0;
        flex-grow: 0;
        flex-shrink: 1;

        @media print {
            margin-right: -0.22rem;
            vertical-align: top;
        }
    }
    ${injectCss}
`;

const isMaxScreenSize = (value: number) => window && window.matchMedia(`(max-width: ${value}px)`).matches;

/**
 * GridContainer provides a 12-column grid scaffolding for GridItem components.
 */
const GridContainer: React.FC<GridContainerProps> = ({
    children,
    css,
    offsetCss,
    offsetProps,
    gap = 30,
    gapMobile,
    ...otherProps
}) => {
    /**
     * XNG-Changes: support gapMobile
     */
    const updateGapValue = () => {
        if (isMobileScreen()) {
            return gapMobile ?? gap ?? 30;
        }
        return gap ?? 30;
    };

    const [customGap, setCustomGap] = useState(updateGapValue());
    const theme = useContext(ThemeContext);

    // <XNG-Changes>
    const gapCalculation = () => {
        const gap = updateGapValue();
        const { maxWidths, containerPadding } = theme.breakpoints || baseTheme.breakpoints;
        const maxWidth = maxWidths[maxWidths.length - 1];
        const padding = containerPadding ? containerPadding[containerPadding.length - 1] : 16;
        const paddingsSum = 2 * padding;
        if (isMaxScreenSize(maxWidth + gap) && gap > paddingsSum) {
            setCustomGap(paddingsSum);
        } else {
            setCustomGap(gap);
        }
    };

    useEffect(() => {
        // to handle gap when window resize
        window.addEventListener("resize", gapCalculation, { passive: true });
        gapCalculation();
        return () => window.removeEventListener("resize", gapCalculation);
    }, []);
    // </XNG-Changes>

    if (!React.Children.count(children)) {
        return null;
    }

    return (
        <GridWrapper css={css} {...otherProps} gap={customGap ?? 30}>
            <GridContext.Provider value={{ gap: customGap ?? 30 }}>
                <GridOffset {...offsetProps} css={offsetCss} gap={customGap ?? 30}>
                    {children}
                </GridOffset>
            </GridContext.Provider>
        </GridWrapper>
    );
};

/** @component */
export default GridContainer;

export { GridOffset, GridWrapper };
